import { useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import api, { formatApiError } from "../lib/api";
import { CheckCircle2, AlertCircle } from "lucide-react";

export function UnsubscribeModal({ open, onOpenChange }) {
    const [contact, setContact] = useState("");
    const [channel, setChannel] = useState("both");
    const [status, setStatus] = useState(null); // {ok, msg}
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus(null);
        try {
            const { data } = await api.post("/unsubscribe", { contact, channel });
            // Only show success if the server confirms success
            if (data.success) {
                setStatus({ ok: true, msg: data.message });
            } else {
                setStatus({ ok: false, msg: "Unable to unsubscribe. Please check your details." });
            }
        } catch (err) {
            setStatus({ ok: false, msg: formatApiError(err.response?.data?.detail) });
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setContact(""); setChannel("both"); setStatus(null);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
            <DialogContent className="border-2 border-ink bg-white sm:max-w-md" data-testid="unsubscribe-modal">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl font-bold">Unsubscribe</DialogTitle>
                    <DialogDescription className="font-sans text-sm text-muted-foreground">
                        Enter the email or phone number on your account and choose what to stop receiving.
                    </DialogDescription>
                </DialogHeader>

                {status?.ok ? (
                    <div className="flex items-start gap-3 border border-pos p-4" data-testid="unsubscribe-success">
                        <CheckCircle2 className="text-pos shrink-0 mt-0.5" size={20} />
                        <p className="text-sm">{status.msg}</p>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label className="font-mono text-[11px] uppercase tracking-wider block mb-1">Email or phone</label>
                            <input
                                required
                                value={contact}
                                onChange={(e) => setContact(e.target.value)}
                                placeholder="you@example.com or +1 555 123 4567"
                                data-testid="unsubscribe-contact"
                                className="w-full border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
                            />
                        </div>
                        <div>
                            <label className="font-mono text-[11px] uppercase tracking-wider block mb-2">Unsubscribe from</label>
                            <div className="grid grid-cols-3 gap-2">
                                {["email", "sms", "both"].map((c) => (
                                    <button
                                        type="button"
                                        key={c}
                                        onClick={() => setChannel(c)}
                                        data-testid={`unsubscribe-channel-${c}`}
                                        className={`py-2 font-mono text-xs uppercase tracking-wider border transition-colors duration-150 ${
                                            channel === c ? "bg-ink text-white border-ink" : "border-ink hover:bg-muted"
                                        }`}
                                    >
                                        {c}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {status && !status.ok && (
                            <div className="flex items-start gap-2 border border-neg p-3" data-testid="unsubscribe-error">
                                <AlertCircle className="text-neg shrink-0 mt-0.5" size={18} />
                                <p className="text-sm text-neg">{status.msg}</p>
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="unsubscribe-submit"
                            className="w-full bg-accent text-white py-3 font-mono text-xs uppercase tracking-wider hover:bg-ink transition-colors duration-150 disabled:opacity-50"
                        >
                            {loading ? "Processing..." : "Unsubscribe"}
                        </button>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
